// apps/admin-ui/src/components/charts/Sparkline.tsx
// CSS-only sparkline chart using flexbox bars
// Zero JavaScript - pure CSS for instant render
// ALL RIGHTS RESERVED

import '../../styles/charts.css';

export interface SparklineProps {
  values: number[];     // Array of values to display
  color?: string;       // CSS color or variable (default: primary)
  height?: number;      // Height in pixels (default: 32)
  label?: string;       // Accessibility label
}

/**
 * Sparkline Chart
 * 
 * Renders a mini bar chart using CSS flexbox.
 * Zero CPU overhead - no canvas, no JS.
 * 
 * Features:
 * - Pure CSS rendering with flexbox
 * - Automatic scaling to max value
 * - Responsive width
 */
export function Sparkline({ 
  values, 
  color = 'var(--color-primary)',
  height = 32,
  label,
}: SparklineProps) {
  // Calculate max value for scaling
  const maxValue = Math.max(...values, 1);
  
  // Calculate percentages for each bar
  const percentages = values.map(v => (v / maxValue) * 100);

  return (
    <div 
      className="sparkline"
      style={{ height: `${height}px` }}
      role="img"
      aria-label={label || `Sparkline showing ${values.length} data points`}
    >
      {percentages.map((percentage, index) => (
        <div
          key={index}
          className="spark-bar"
          style={{
            '--value': `${percentage}%`,
            '--color': color,
          } as React.CSSProperties}
          title={`Value: ${values[index]}`}
        />
      ))}
    </div>
  );
}

export default Sparkline;
